/**
 * LeadsDashboardView.jsx - the Leads Dashboard (Paul's D-01..D-06): lead KPIs
 * for any period, for anyone to review.
 *
 * Everything is computed in the browser from what AppContext already holds
 * (every account with its own stage and status history, and every
 * opportunity) plus one fetch of the open lead tasks for the No next step
 * list. There is no dashboard API. The rules are components/crm/leadsDashboard.js
 * and the sections are components/crm/LeadsDashboardParts.jsx.
 *
 * Two kinds of number:
 *   - period numbers (the KPI cards, Entered, median days, Went Cold,
 *     Reactivated, source performance) count what happened between the
 *     resolved dates shown in the header;
 *   - live numbers (Now, Cold now, Stalled, No next step) are the Leads board
 *     as it is right now. They follow the filters but not the period, and are
 *     tagged "Live" so a number that holds still is not read as a bug.
 * The period and the four filters are remembered per user in this browser.
 * Clicking a lead opens it on the Leads page (#/leads?account=<id>).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { KpiCard } from '../components/ui/KpiCard';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { Select } from '../components/ui/Select';
import { buildLeadCards } from '../components/crm/leadsBoard';
import { useTasksChanged } from '../components/tasks/useTasks';
import { latestReads } from '../live/useLive';
import { fmtDate } from '../utils/format';
import {
  ALL,
  DASH_PERIODS,
  DEFAULT_DASH_FILTERS,
  STATUS_HISTORY_SINCE,
  localDay,
  periodRange,
  periodLabel,
  fmtRange,
  sanitizeDashPrefs,
  dashUnits,
  matchesDash,
  dashFilterOptions,
  kpiCounts,
  stageMovement,
  coldSummary,
  sourcePerformance,
  stalledLeads,
  undatedActiveLeads,
  noNextStepLeads,
  stageHistorySince,
} from '../components/crm/leadsDashboard';
import {
  CountedLists,
  StageMovementCard,
  ColdSummaryCard,
  SourcePerformanceCard,
  StalledCard,
  NoNextStepLeadsCard,
} from '../components/crm/LeadsDashboardParts';

const FONT = "'Montserrat', sans-serif";
const LABEL = { fontSize: 9, fontWeight: 800, color: '#888', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 4 };
const DATE_INPUT = {
  border: '1px solid #D8DED4',
  borderRadius: 6,
  padding: '5px 8px',
  fontSize: 11,
  fontFamily: FONT,
  color: '#0D0D0D',
  background: '#fff',
};
const FILTER_KEYS = ['owner', 'tier', 'vertical', 'source'];

/* ── remembered choices ──────────────────────────────────────────────────
 * Per user, per browser: { period, from, to, owner, tier, vertical, source }
 * as JSON. Storage can be blocked or empty - the page then opens on the
 * defaults, and a choice that could not be saved is simply not remembered. */
const prefKey = (email) => `globalops_leadsdash:${String(email || '').toLowerCase()}`;
function readPrefs(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return sanitizeDashPrefs(raw ? JSON.parse(raw) : null);
  } catch {
    return sanitizeDashPrefs(null);
  }
}
function useDashPrefs(email) {
  const key = prefKey(email);
  const [prefs, setPrefsState] = useState(() => readPrefs(key));
  useEffect(() => {
    setPrefsState(readPrefs(key));
  }, [key]);
  const setPrefs = useCallback(
    (patch) => {
      setPrefsState((cur) => {
        const next = sanitizeDashPrefs({ ...cur, ...patch });
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* not remembered this time */
        }
        return next;
      });
    },
    [key]
  );
  return [prefs, setPrefs];
}

/* ── the open lead tasks (No next step) ──────────────────────────────────
 * The same request the Leads page makes. A refusal or a network error is
 * shown on the card; until a list has arrived the lead cards carry hasTask
 * null, so no lead is wrongly listed as having no next step.
 *
 * A read that fails keeps the last list (a 429 or 502 while every open tab
 * re-reads after someone's change must not wipe the dashboard), and only a
 * read the person is waiting for - the first, Try again - shows the error.
 * Reads can overlap, so the newest answer GOT wins (latestReads). */
function useOpenLeadTasks() {
  const [tasks, setTasks] = useState({ rows: null, status: 'loading', error: null });
  const alive = useRef(true);
  const [reads] = useState(latestReads);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const load = useCallback(
    ({ live } = {}) => {
      const ticket = reads.start();
      fetch('/api/crm-actions?status=open&entity_type=account')
        .then(async (r) => {
          if (!r.ok) {
            let msg = '';
            try {
              msg = (await r.json()).error || '';
            } catch {
              /* no JSON body */
            }
            throw new Error(msg || (r.status === 403 ? 'You do not have access to tasks.' : `The server answered ${r.status}.`));
          }
          const rows = await r.json();
          if (!Array.isArray(rows)) throw new Error('The server sent something unexpected.');
          return rows;
        })
        .then((rows) => {
          if (alive.current && reads.show(ticket)) setTasks({ rows, status: 'ok', error: null });
        })
        .catch((err) => {
          console.error('Leads Dashboard: open tasks failed to load', err);
          if (!alive.current || live || !reads.show(ticket)) return;
          setTasks((t) => ({ ...t, status: 'error', error: (err && err.message) || 'Network error.' }));
        });
    },
    [reads]
  );
  const retry = useCallback(() => {
    setTasks((t) => ({ ...t, status: 'loading', error: null }));
    load();
  }, [load]);
  useEffect(load, [load]);
  useTasksChanged(load);
  return [tasks, retry];
}

function FilterSelect({ label, value, options, onChange }) {
  // a remembered pick stays listed even when no lead carries it any more
  const list = options.includes(value) ? options : [...options, value];
  return (
    <label style={{ display: 'flex', flexDirection: 'column', minWidth: 140 }}>
      <span style={LABEL}>{label}</span>
      <Select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} style={{ fontFamily: FONT }}>
        {list.map((o) => (
          <option key={o} value={o}>
            {o === ALL ? 'All' : o}
          </option>
        ))}
      </Select>
    </label>
  );
}

export default function LeadsDashboardView() {
  const { allAccounts, allProjects, loading, resolveOwnerName } = useApp();
  const { user } = useAuth();
  const [prefs, setPrefs] = useDashPrefs(user?.email);
  const [tasks, retryTasks] = useOpenLeadTasks();

  const today = localDay(new Date());
  const range = useMemo(
    () => periodRange(prefs.period, today, { from: prefs.from, to: prefs.to }),
    [prefs.period, prefs.from, prefs.to, today]
  );
  // lead owners are display names already; an email owner is looked up (as on the Dashboard)
  const ownerName = useCallback(
    (o) => (o && resolveOwnerName && String(o).includes('@') ? resolveOwnerName(o) : o) || '',
    [resolveOwnerName]
  );
  const filters = useMemo(
    () => ({ owner: prefs.owner, tier: prefs.tier, vertical: prefs.vertical, source: prefs.source }),
    [prefs.owner, prefs.tier, prefs.vertical, prefs.source]
  );

  const units = useMemo(() => dashUnits(allAccounts, allProjects), [allAccounts, allProjects]);
  const cards = useMemo(
    () => buildLeadCards(allAccounts, { projects: allProjects, actions: tasks.rows }),
    [allAccounts, allProjects, tasks.rows]
  );
  const options = useMemo(() => dashFilterOptions(units, ownerName), [units, ownerName]);
  const shownUnits = useMemo(() => units.filter((u) => matchesDash(u, filters, ownerName)), [units, filters, ownerName]);
  const shownCards = useMemo(() => cards.filter((c) => matchesDash(c, filters, ownerName)), [cards, filters, ownerName]);

  const kpis = useMemo(() => kpiCounts(shownUnits, range), [shownUnits, range]);
  const movement = useMemo(() => stageMovement(shownUnits, shownCards, range), [shownUnits, shownCards, range]);
  const cold = useMemo(() => coldSummary(shownUnits, shownCards, range), [shownUnits, shownCards, range]);
  const perf = useMemo(
    () => sourcePerformance(shownUnits, range, { allSources: filters.source === ALL }),
    [shownUnits, range, filters.source]
  );
  const stalled = useMemo(() => stalledLeads(shownCards), [shownCards]);
  const undated = useMemo(() => undatedActiveLeads(shownCards), [shownCards]);
  const noNext = useMemo(() => noNextStepLeads(shownCards), [shownCards]);
  const stageSince = useMemo(() => stageHistorySince(allAccounts), [allAccounts]);

  const filtered = FILTER_KEYS.some((k) => filters[k] !== ALL);
  const pickPeriod = (period) => {
    // Custom starts from the dates already on screen
    if (period === 'custom' && prefs.period !== 'custom') setPrefs({ period, from: range.from, to: range.to });
    else setPrefs({ period });
  };
  // store the pair as shown (already swapped if it was entered backwards) plus the one date changed
  const setCustom = (patch) => setPrefs({ from: range.from, to: range.to, ...patch });

  if (loading && !(allAccounts && allAccounts.length)) {
    return (
      <div aria-busy="true" aria-label="Loading the Leads Dashboard">
        <div className="kpi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="go-skel" style={{ height: 96 }} />
          ))}
        </div>
        <div className="go-skel" style={{ height: 260 }} />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT }}>
      {/* header: the period and its resolved dates, always shown (D-01) */}
      <div
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}
      >
        <div>
          <div className="text-eyebrow uppercase text-text-muted mb-1">Workspace · Leads Dashboard</div>
          <p className="text-body-sm text-text-body m-0">
            <b className="text-text-dark">{periodLabel(prefs.period)}</b>
            {': '}
            <span className="tabular-nums" data-testid="dash-range">
              {fmtRange(range)}
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <SegmentedControl options={DASH_PERIODS} value={prefs.period} onChange={pickPeriod} className="flex-wrap" />
          {prefs.period === 'custom' && (
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="date"
                aria-label="From"
                style={DATE_INPUT}
                value={range.from}
                onChange={(e) => setCustom({ from: e.target.value })}
              />
              <span style={{ fontSize: 11, color: '#888' }}>to</span>
              <input type="date" aria-label="To" style={DATE_INPUT} value={range.to} onChange={(e) => setCustom({ to: e.target.value })} />
            </span>
          )}
        </div>
      </div>

      {/* filters: Owner / Tier / Vertical / Lead source, on each lead's current values */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
        <FilterSelect label="Owner" value={prefs.owner} options={options.owners} onChange={(v) => setPrefs({ owner: v })} />
        <FilterSelect label="Tier" value={prefs.tier} options={options.tiers} onChange={(v) => setPrefs({ tier: v })} />
        <FilterSelect label="Vertical" value={prefs.vertical} options={options.verticals} onChange={(v) => setPrefs({ vertical: v })} />
        <FilterSelect label="Lead source" value={prefs.source} options={options.sources} onChange={(v) => setPrefs({ source: v })} />
        {filtered && (
          <button
            type="button"
            onClick={() => setPrefs(DEFAULT_DASH_FILTERS)}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid #D8DED4',
              background: '#fff',
              color: '#1B5E20',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: FONT,
            }}
          >
            Clear filters
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#999', alignSelf: 'center' }}>
          {shownUnits.length} of {units.length} sites and accounts{filtered ? ' match the filters' : ''}
        </span>
      </div>

      {!loading && units.length === 0 && (
        <div
          role="status"
          style={{
            padding: '10px 14px',
            marginBottom: 16,
            borderRadius: 8,
            background: '#FFF8EC',
            border: '1px solid #FFE0B2',
            color: '#8A5A00',
            fontSize: 11,
          }}
        >
          No accounts are loaded, so every number below is 0. Refresh the page if you expected data.
        </div>
      )}

      {/* D-02 */}
      <div
        role="group"
        aria-label="Lead KPIs"
        className="kpi-grid-4"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 12 }}
      >
        <KpiCard
          label="New leads"
          value={kpis.newLeads}
          accent="blue"
          delta="Sites and accounts created"
          deltaTone="neutral"
          explain={{
            body: 'Sites, and accounts with no sites, created during the period. A record created as a Customer is not counted, and neither are the records loaded by the original data import. A company that has sites is counted through its sites.',
            formula: 'count(site or site-less account)\n  where created in period\n  and not created as Customer',
          }}
        />
        <KpiCard
          label="Discovery meetings held"
          value={kpis.meetings}
          accent="green"
          delta="Entries into Discovery Meeting Complete"
          deltaTone="neutral"
          explain={{
            body: 'Leads that moved into Discovery Meeting Complete during the period, from stage history. Each lead counts once, even if its card moved back and forth.',
            formula: 'count distinct leads with a stage history entry\n  into Discovery Meeting Complete in period',
          }}
        />
        <KpiCard
          label="NDAs executed"
          value={kpis.ndas}
          accent="greenBright"
          delta="Entries into NDA Executed"
          deltaTone="neutral"
          explain={{
            body: 'Leads that moved into NDA Executed during the period, from stage history. Each lead counts once.',
            formula: 'count distinct leads with a stage history entry\n  into NDA Executed in period',
          }}
        />
        <KpiCard
          label="Leads with first opportunity"
          value={kpis.firstOpps}
          accent="amber"
          delta="First opportunity on a Lead site"
          deltaTone="neutral"
          explain={{
            body: 'Leads whose first opportunity falls in the period while the site was still a Lead. Only opportunities linked to the exact site count. An opportunity added in the app is dated by the day it was created. One that was loaded into GlobalOps before it became the source of truth is dated by its quote date, or the day it was added when it has none.',
            formula:
              'count(lead) where min(day of linked opportunities) in period\n  and designation on that day = Lead\nday = created day (added in the app), else quote date',
          }}
        />
      </div>
      <CountedLists lists={kpis.lists} ownerName={ownerName} />

      {/* D-03 beside D-04; they stack on a narrower screen */}
      <div
        className="dashboard-grid-main"
        style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16, marginBottom: 16, alignItems: 'start' }}
      >
        <StageMovementCard rows={movement} />
        <ColdSummaryCard cold={cold} ownerName={ownerName} />
      </div>

      {/* D-05 */}
      <SourcePerformanceCard perf={perf} />

      {/* D-06 */}
      <div
        className="dashboard-grid-main"
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16, alignItems: 'start' }}
      >
        <StalledCard rows={stalled} undated={undated} ownerName={ownerName} />
        <NoNextStepLeadsCard rows={noNext} tasks={tasks} onRetry={retryTasks} ownerName={ownerName} />
      </div>

      <p style={{ fontSize: 10, color: '#8A8A8A', margin: '4px 0 0', lineHeight: 1.6 }}>
        {stageSince ? `Stage history since ${fmtDate(stageSince)}.` : 'No stage history has been recorded yet.'}
        {` Status history (designation and Cold / Active changes) since ${fmtDate(STATUS_HISTORY_SINCE)}.`}
        {
          " Filters use each lead's current owner, tier, vertical and lead source. Numbers tagged Live are the Leads board right now and do not change with the period."
        }
      </p>
    </div>
  );
}
